import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEvent, createStore, createEffect, sample } from "effector";
import { ICurrentCourseContract } from "@features/courses/ui/current";
import { IUserAuthContract } from "@features/student/auth/model";
import { ICoursesContract } from "@features/courses/model";
import { routePathNameChanged } from "@features/app/model";
import { StudentApi } from "@features/shared/api/course-data.api";
import { encodeCertificateId, encodeRoadmapCertificateId } from "@utils/certificate-id";
import { getCertificateUrl, getCertificatePageUrl } from "@utils/certificate-url";
import { currentCertificateLang, type CertificateLang } from "@utils/certificate-lang";
import { formatCertificateDate } from "@utils/certificate-date";
import type { CertificateData } from "@features/shared/api/certificate.api";
import { certificateImageShared } from "@features/shared/events";

export const certificateModalClosed = createEvent();
const certificateModalOpenChanged = createEvent<boolean>();
export const shareCertificateClicked = createEvent<{ id: string; courseName: string; pageUrl?: string }>();
export const shareImageClicked = createEvent<string>();
const certificateNameInputOpenChanged = createEvent<boolean>();
const certificateFirstNameChanged = createEvent<string>();
const certificateLastNameChanged = createEvent<string>();
const saveCertificateNameRequested = createEvent();
const shareCertificateSheetOpenChanged = createEvent<{ open: boolean; courseId?: number; roadmapId?: number; roadmapName?: string; roadmapCourseLength?: string }>();
const certificateDataExternalChanged = createEvent<CertificateData>();
export const certificateLangChanged = createEvent<CertificateLang>();

const $isCertificateModalOpen = createStore(false)
    .on(certificateModalOpenChanged, (_, p) => p)
    .reset(routePathNameChanged);

const $isCertificateNameInputOpen = createStore(false)
    .on(certificateNameInputOpenChanged, (_, p) => p)
    .reset(routePathNameChanged);

const $certificateFirstName = createStore("")
    .on(certificateFirstNameChanged, (_, p) => p)
    .reset(routePathNameChanged);

const $certificateLastName = createStore("")
    .on(certificateLastNameChanged, (_, p) => p)
    .reset(routePathNameChanged);

const $isShareCertificateSheetOpen = createStore(false)
    .on(shareCertificateSheetOpenChanged, (_, { open }) => open)
    .reset(routePathNameChanged);

const $certificateData = createStore<CertificateData | null>(null)
    .on(certificateDataExternalChanged, (_, data) => data)
    .reset(routePathNameChanged);

// Language the certificate image/PDF/share link are generated in. Defaults to
// the product UI language whenever a certificate surface opens; the user can
// override it with the language picker on those screens.
const $certificateLang = createStore<CertificateLang>("en").on(certificateLangChanged, (_, lang) => lang);

const $shareCourseId = createStore<number | null>(null)
    .on(shareCertificateSheetOpenChanged, (_, { courseId }) => courseId ?? null)
    .reset(routePathNameChanged);

const $shareRoadmapId = createStore<number | null>(null)
    .on(shareCertificateSheetOpenChanged, (_, { roadmapId }) => roadmapId ?? null)
    .reset(routePathNameChanged);

const $shareRoadmapName = createStore<string>("")
    .on(shareCertificateSheetOpenChanged, (_, { roadmapName }) => roadmapName ?? "")
    .reset(routePathNameChanged);

const $shareRoadmapCourseLength = createStore<string>("")
    .on(shareCertificateSheetOpenChanged, (_, { roadmapCourseLength }) => roadmapCourseLength ?? "")
    .reset(routePathNameChanged);

const saveCertificateNameFx = createEffect(async ({ firstName, lastName }: { firstName: string; lastName?: string }) => {
    await StudentApi.updateProfile({ firstName, lastName: (lastName ?? "") });
});

const SHARE_TEXTS = [
    "Proud moment—completed [Course Name] and earned this certificate 🎓",
    "Just ticked off [Course Name] from my learning list. Certificate unlocked!",
    "Big step forward: my [Course Name] certificate is here 🚀",
    "Learning journey update → finished [Course Name]!",
    "Another milestone achieved. [Course Name] certificate earned 🙌",
    "Excited to share my [Course Name] certificate today!",
    "Done and dusted: [Course Name] complete, certificate in hand 🎉",
    "Skills upgraded. Got my [Course Name] certificate 🔑",
    "Official proof of progress → [Course Name] certificate:"
];

const getRandomShareText = (courseName: string): string => {
    const randomText = SHARE_TEXTS[Math.floor(Math.random() * SHARE_TEXTS.length)];
    return randomText.replace('[Course Name]', courseName);
};

const shareCertificateFx = createEffect<{ id: string; courseName: string; pageUrl?: string }, void>(({ id, courseName, pageUrl }) => {
    if (typeof window === "undefined") return;
    const origin = window.location.origin;
    const url = pageUrl || `${origin}/certificate/${id}`;
    const text = getRandomShareText(courseName);
    const shareData = {
        text: `${text} ${url}`,
    };

    if (typeof navigator !== "undefined" && (navigator as any).share) {
        (navigator as any).share(shareData).catch(() => { });
        return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
});

const shareImageFx = createEffect<
    { certificateId: string; certificateData: CertificateData | null; firstName: string; lastName: string; lang: CertificateLang },
    void
>(async ({ certificateId, certificateData, firstName, lastName, lang }) => {
    if (typeof window === "undefined") return;


    const isRoadmapCert = certificateData?.type === "roadmap";
    const certificateImg = getCertificateUrl(certificateId, {
        firstName,
        lastName,
        baseUrl: window.location.origin,
        type: isRoadmapCert ? "roadmap" : "course",
        courseName: isRoadmapCert ? certificateData?.courseName ?? null : null,
        date: isRoadmapCert ? certificateData?.date ?? null : null,
        courseLength: isRoadmapCert ? certificateData?.courseLength ?? null : null,
        lang
    });

    if (!("share" in navigator) || !("canShare" in navigator)) {
        throw new Error("Web Share with files is not supported on this platform.");
    }

    certificateImageShared(certificateId);

    const res = await fetch(certificateImg, { mode: "cors", credentials: "omit", cache: "no-store" });
    const blob = await res.blob();
    const type = blob.type || "image/png";
    const safeName = `tixu-certificate-${certificateData?.id}.png`;
    const file = new File([blob], safeName, { type });

    if (!(navigator as any).canShare({ files: [file] })) {
        throw new Error("This target cannot share image files.");
    }

    await (navigator as any).share({ files: [file] });
});

const contract = new ModelContract(
    { $isCertificateModalOpen, $isCertificateNameInputOpen, $certificateFirstName, $certificateLastName, $saveCertificateNamePending: saveCertificateNameFx.pending, $isShareCertificateSheetOpen, $certificateData, $shareCourseId, $certificateLang },
    { certificateModalClosed, certificateModalOpenChanged, shareCertificateClicked, shareImageClicked, certificateNameInputOpenChanged, certificateFirstNameChanged, certificateLastNameChanged, saveCertificateNameRequested, shareCertificateSheetOpenChanged, certificateDataExternalChanged, certificateLangChanged },
    {},
    { shareCertificateFx, shareImageFx }
);

export type ICertificateModalContract = typeof contract;

export class CertificateModalModule extends ModuleDescription<ICertificateModalContract> {
    constructor(
        public currentCourseModule: ICurrentCourseContract,
        public userAuthModule: IUserAuthContract,
        public coursesModule: ICoursesContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        // Default the certificate language to the product UI language when the
        // certificate popup opens.
        sample({
            clock: certificateModalOpenChanged,
            filter: (open) => open,
            fn: () => currentCertificateLang(),
            target: $certificateLang
        });

        // Same for the share sheet — unless it is opened from the certificate
        // popup, which is already showing a (possibly user-picked) language.
        sample({
            source: $isCertificateModalOpen,
            clock: shareCertificateSheetOpenChanged,
            filter: (popupOpen, { open }) => Boolean(open) && !popupOpen,
            fn: () => currentCertificateLang(),
            target: $certificateLang
        });

        sample({
            clock: this.currentCourseModule.stores.$isCourseFeedbackModalVisble,
            filter: (visible) => Boolean(visible),
            fn: () => true,
            target: certificateModalOpenChanged
        });

        sample({
            clock: certificateModalClosed,
            fn: () => false,
            target: certificateModalOpenChanged
        });

        sample({
            clock: certificateModalClosed,
            fn: () => false,
            target: certificateNameInputOpenChanged
        });

        sample({
            clock: certificateModalClosed,
            fn: () => ({ open: false }),
            target: shareCertificateSheetOpenChanged
        });

        sample({
            source: this.userAuthModule.stores.$student,
            clock: shareCertificateSheetOpenChanged,
            filter: (_, { open }) => Boolean(open),
            fn: (student) => student?.firstname ?? "",
            target: certificateFirstNameChanged
        });

        sample({
            source: this.userAuthModule.stores.$student,
            clock: shareCertificateSheetOpenChanged,
            filter: (_, { open }) => Boolean(open),
            fn: (student) => student?.lastname ?? "",
            target: certificateLastNameChanged
        });

        // If ShareCertificateSheet requested but user name is missing -> open name sheet instead and keep share sheet closed
        sample({
            source: { student: this.userAuthModule.stores.$student },
            clock: shareCertificateSheetOpenChanged,
            filter: ({ student }, { open }) => Boolean(open && (!student?.firstname)),
            fn: () => true,
            target: certificateNameInputOpenChanged
        });

        // Reopen ShareCertificateSheet after saving name if it was requested (course)
        sample({
            clock: saveCertificateNameFx.done,
            source: { shareCourseId: $shareCourseId, shareRoadmapId: $shareRoadmapId },
            filter: ({ shareCourseId, shareRoadmapId }) => shareCourseId !== null && shareRoadmapId === null,
            fn: ({ shareCourseId }) => ({ open: true, courseId: shareCourseId ?? undefined }),
            target: shareCertificateSheetOpenChanged
        });

        // Reopen ShareCertificateSheet after saving name if it was requested (roadmap)
        sample({
            clock: saveCertificateNameFx.done,
            source: { shareRoadmapId: $shareRoadmapId, shareRoadmapName: $shareRoadmapName, shareRoadmapCourseLength: $shareRoadmapCourseLength },
            filter: ({ shareRoadmapId }) => shareRoadmapId !== null,
            fn: ({ shareRoadmapId, shareRoadmapName, shareRoadmapCourseLength }) => ({ open: true, roadmapId: shareRoadmapId ?? undefined, roadmapName: shareRoadmapName, roadmapCourseLength: shareRoadmapCourseLength }),
            target: shareCertificateSheetOpenChanged
        });

        // Handle certificate data generation for current course
        sample({
            source: { student: this.userAuthModule.stores.$student, course: this.currentCourseModule.stores.$currentCourse },
            clock: shareCertificateSheetOpenChanged,
            filter: ({ student, course }, { open, courseId, roadmapId }) => Boolean(open && student && course && student.id && course.id && !courseId && !roadmapId),
            fn: ({ student, course }) => {
                const id = encodeCertificateId(student!.id as number, course!.id as number);
                const nowIso = new Date().toISOString();
                const data: CertificateData = {
                    id,
                    firstName: student?.firstname ?? null,
                    lastName: (student?.lastname ?? "") || null,
                    courseName: course?.title ?? "",
                    date: nowIso,
                    courseLength: "",
                    type: "course"
                };
                return data;
            },
            target: $certificateData
        });

        // Handle certificate data generation for specific courseId
        sample({
            source: { student: this.userAuthModule.stores.$student, courses: this.coursesModule.stores.$courses },
            clock: shareCertificateSheetOpenChanged,
            filter: ({ student }, { open, courseId, roadmapId }) => Boolean(open && student && student.id && courseId && !roadmapId),
            fn: ({ student, courses }, { courseId }) => {
                const course = courses.find((c: any) => c.id === courseId);
                if (!course) return null;

                const id = encodeCertificateId(student!.id as number, courseId!);
                const nowIso = new Date().toISOString();
                const data: CertificateData = {
                    id,
                    firstName: student?.firstname ?? null,
                    lastName: (student?.lastname ?? "") || null,
                    courseName: course.title ?? "",
                    date: nowIso,
                    courseLength: "",
                    type: "course"
                };
                return data;
            },
            target: $certificateData
        });

        // Handle certificate data generation for a roadmap (frontend-only, no backend lookup)
        sample({
            source: { student: this.userAuthModule.stores.$student },
            clock: shareCertificateSheetOpenChanged,
            filter: ({ student }, { open, roadmapId }) => Boolean(open && student && student.id && roadmapId),
            fn: ({ student }, { roadmapId, roadmapName, roadmapCourseLength }) => {
                const id = encodeRoadmapCertificateId(student!.id as number, roadmapId!);
                const data: CertificateData = {
                    id,
                    firstName: student?.firstname ?? null,
                    lastName: (student?.lastname ?? "") || null,
                    courseName: roadmapName ?? "",
                    date: formatCertificateDate(new Date()),
                    courseLength: roadmapCourseLength ?? "",
                    type: "roadmap"
                };
                return data;
            },
            target: $certificateData
        });

        sample({
            source: this.userAuthModule.stores.$student,
            clock: certificateNameInputOpenChanged,
            filter: (_, open) => Boolean(open),
            fn: (student) => student?.firstname ?? "",
            target: certificateFirstNameChanged
        });

        sample({
            source: this.userAuthModule.stores.$student,
            clock: certificateNameInputOpenChanged,
            filter: (_, open) => Boolean(open),
            fn: (student) => student?.lastname ?? "",
            target: certificateLastNameChanged
        });

        sample({
            clock: shareCertificateClicked,
            target: shareCertificateFx
        });

        sample({
            source: { certificateData: $certificateData, firstName: $certificateFirstName, lastName: $certificateLastName, lang: $certificateLang },
            clock: shareImageClicked,
            fn: ({ certificateData, firstName, lastName, lang }, certificateId) => ({
                certificateId,
                certificateData,
                firstName,
                lastName,
                lang
            }),
            target: shareImageFx
        });

        sample({
            source: { firstName: $certificateFirstName, lastName: $certificateLastName },
            clock: saveCertificateNameRequested,
            fn: ({ firstName, lastName }) => ({ firstName, lastName }),
            target: saveCertificateNameFx
        });

        sample({
            source: { data: $certificateData, firstName: $certificateFirstName, lastName: $certificateLastName },
            clock: saveCertificateNameFx.done,
            fn: ({ data, firstName, lastName }) => (data ? { ...data, firstName, lastName } : data),
            target: $certificateData
        });

        sample({
            source: { student: this.userAuthModule.stores.$student, firstName: $certificateFirstName, lastName: $certificateLastName },
            clock: saveCertificateNameRequested,
            filter: ({ student }) => student !== null,
            fn: ({ student, firstName, lastName }) => ({ ...student!, firstname: firstName, lastname: lastName }),
            target: this.userAuthModule.stores.$student
        });

        sample({
            clock: saveCertificateNameFx.done,
            fn: () => false,
            target: certificateNameInputOpenChanged
        });
    }
}


